// src/api/healthcare/ConsultGenerationApi.ts

import { ENV } from "../../../config/runtimeEnv";
import type { UserUuid } from "../../../types/identity";
import { http } from "../../../lib/http/client";

/**
 * The browser's ONE way into a consult generation.
 *
 * ═══ WHY THIS MODULE EXISTS ══════════════════════════════════════════════════════════════
 * `talk` used to hold every AI call a consultation made — the report, the three record
 * drafts and the live suggestion engine. It holds none of them now: the prompts moved into
 * `xoe-agent` (ClickUp 86d3r82eh, M19–M24) and `talk` no longer generates anything at all.
 * The browser therefore calls healthcare directly, and healthcare relays to that clinic's
 * agent.
 *
 * The relay is `ConsultGenerationController` — **a closed allow-list, not a generic proxy**.
 * Only the keys named in its `timeouts()` are reachable; anything else is a 422 that never
 * touches the network. Two credentials, one hop each: the practitioner's own bearer gets
 * into healthcare, healthcare's `client_credentials` token gets out to the agent.
 *
 * ═══ ⚠️ `VITE_HEALTHCARE_SERVER` ALREADY ENDS IN `/api` ══════════════════════════════════
 * Every ConfigMap sets it to `https://healthcare.<host>/api` (verified across all six), which
 * is why every sibling module in `src/api/healthcare/` writes `${BASE}/practitioner/...` and
 * never `${BASE}/api/practitioner/...`. Adding the prefix here would 404 in every
 * environment — and it would 404 identically in dev, so nothing local would catch it.
 */

const API_BASE_URL = ENV.VITE_HEALTHCARE_SERVER;

/** This repo has NO axios interceptor — no `interceptors.*`, no `axios.defaults` anywhere in
 *  `src/` — so every module attaches the bearer by hand. Same shape as `ConsultApi.ts:8-13`. */

/**
 * The agent's response envelope, relayed VERBATIM by healthcare (decision D11 —
 * `metadata.status` is not inspected on the way through).
 *
 * ⚠️ `data` is `null` on EVERY failure, never `{}`. That is deliberate agent-side: a `{}` is
 * stored happily by a caller as a real-but-empty clinical artefact, and then reads as "the
 * model produced nothing" rather than "the call failed".
 */
export interface GenerationEnvelope<T = unknown> {
	data: T | null;
	citations: unknown[];
	metadata: { status: "complete" | "error"; error?: string | null; [k: string]: unknown };
}

export interface GenerateArgs {
	/** A key on the relay's allow-list. Anything else is a 422 from healthcare. */
	pipeline: string;
	/** From `runKey()` — see its own note. NEVER derive this from anything stable. */
	runKey: string;
	patientId?: UserUuid | null;
	/** The pipeline's own contract. Healthcare validates this as an opaque array and does
	 *  not restate any prompt's shape; the agent's frozen `input_schema` is the authority. */
	inputs: Record<string, unknown>;
	/** Must exceed the relay's own budget for this pipeline (`timeouts()` + a 10s connect). */
	timeoutMs: number;
}

/**
 * A fresh run key. **Never derive one from a patient id, an appointment id, a suggestion id
 * or the inputs themselves.**
 *
 * The agent's run ledger is keyed on `(pipeline, patient_id, run_key)` and REPLAYS the stored
 * envelope for a repeat. That is right for a queued job being retried; it is wrong here,
 * where a repeat means a practitioner pressing the button again because the first draft was
 * poor. A stable key returns the same stale result forever, with `status: complete` and
 * nothing in any log to say so.
 *
 * `crypto.randomUUID` is undefined outside a secure context and local development is served
 * over plain `http://{MINIKUBE_IP}:3000`, so the fallback is a normal path here rather than
 * an edge — same guard as `utils/consult/participantIdentity.ts` and `hooks/useSchedule.ts`.
 */
export const runKey = (): string => {
	const webcrypto = typeof globalThis !== "undefined" ? (globalThis.crypto as Crypto | undefined) : undefined;
	if (webcrypto && typeof webcrypto.randomUUID === "function") return webcrypto.randomUUID();

	return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
		const r = (Math.random() * 16) | 0;
		const v = c === "x" ? r : (r & 0x3) | 0x8;
		return v.toString(16);
	});
};

/**
 * Relay one generation and hand back the envelope UNCHANGED.
 *
 * ⚠️ **A 200 IS NOT SUCCESS.** Every failure after the request is accepted comes back as
 * HTTP 200 with `metadata.status: 'error'`, `metadata.error` from a closed vocabulary and
 * `data: null`. This function does NOT throw on that — each caller decides, because they
 * genuinely differ: a record draft degrades to an error banner with a Retry button, while a
 * consultation report must abort the save it precedes.
 *
 * What DOES throw is a transport failure or a non-2xx (a 422 from the relay's allow-list, a
 * 502 when the agent is unreachable, a timeout). Those mean the generation did not happen at
 * all, which is a different fact from "the agent declined it" and deserves a different
 * answer.
 *
 * ⚠️ **`documents` and `options` are NEVER sent.** Healthcare validates both as `prohibited`
 * and 422s the whole request — deliberately, so a caller learns from a status code rather
 * than from a behaviour that silently never happened.
 *
 * NO RETRIES. `callAnthropic(payload, retries = 2)` in the old `talk` made up to three
 * attempts at 60s each for a call a practitioner was already waiting on; the whole chain is
 * now sized as one attempt, and a retry here would multiply against nothing that expects it.
 */
export async function generate<T = unknown>(args: GenerateArgs): Promise<GenerationEnvelope<T>> {
	const response = await http.post(
		`${API_BASE_URL}/practitioner/consult-generations`,
		{
			pipeline: args.pipeline,
			run_key: args.runKey,
			patient_id: args.patientId ?? null,
			inputs: args.inputs,
		},
		{
			timeout: args.timeoutMs,
		}
	);

	return response.data as GenerationEnvelope<T>;
}
